import * as Tooltip from '@radix-ui/react-tooltip';
import { cn } from '../../lib/cn';
import { formatSigned } from '../../utils/scoreColor';
import type { LevelState, LevelVariant } from '../../utils/scaleLevels';

export interface ScaleLevelBoxProps {
  score: number;
  label: string;
  state: LevelState;
  variant: LevelVariant;
  /** -- True when the column's current score is negative (red scheme) */
  negativeMode: boolean;
  justification?: string;
  className?: string;
}

export function ScaleLevelBox({
  score,
  label,
  state,
  variant,
  negativeMode,
  justification,
  className,
}: ScaleLevelBoxProps) {
  const isActive = state === 'active';
  const isFilled = state === 'filled';
  const isMuted = state === 'muted';
  const isZero = score === 0;
  const useRed = !isZero && negativeMode && (isActive || (isFilled && score < 0));

  const box = (
    <div
      data-score={score}
      data-state={state}
      data-variant={variant}
      aria-current={isActive ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border px-2.5 py-2 sm:gap-3 sm:px-3 sm:py-2.5',
        'transition-[opacity,filter,box-shadow,background-color,border-color] duration-300',
        // -- Zero = scale baseline: neutral grey with a clear outline
        isZero &&
          cn(
            'border border-atlas-dim bg-atlas-score-0/50 text-atlas-muted',
            isActive &&
              'z-[1] border-2 border-atlas-muted bg-atlas-score-0 text-atlas-bright-text opacity-85',
            isFilled && 'opacity-55',
            isMuted && 'opacity-40'
          ),
        // -- Standard purple ladder box (glow only on the actual score)
        variant === 'standard' &&
          !useRed &&
          !isZero &&
          cn(
            'border-atlas-border/40 bg-atlas-deep/80 text-atlas-white',
            'night:border-purple-400/30 night:bg-[#1a0a28]/90',
            // -- Party floor: full colour + contained glow
            isActive &&
              cn(
                'z-[1] border-2 border-atlas-bloom bg-atlas-rich text-atlas-white shadow-atlas-ladder-active',
                'night:border-fuchsia-400 night:bg-[#3a1860]'
              ),
            // -- Floors below: flat, quiet, no outer bloom
            isFilled &&
              'border border-atlas-border/45 bg-atlas-mid/35 opacity-40 night:border-purple-500/25 night:bg-[#221033]/50',
            isMuted && 'border-transparent opacity-[0.16] grayscale saturate-50'
          ),
        // -- Red negative scheme: keep glow on the selected (active) score
        variant === 'standard' &&
          useRed &&
          cn(
            'text-atlas-white',
            isActive &&
              'z-[1] border-2 border-red-400 bg-red-950/90 shadow-atlas-ladder-red',
            isFilled &&
              'border border-red-800/40 bg-red-950/30 opacity-38 shadow-none'
          ),
        // -- Silver proven (+3/+4 ETS): keep metallic silver; dark ink for contrast
        variant === 'silver' &&
          cn(
            isActive &&
              'z-[1] border-2 border-white bg-atlas-proven text-neutral-900 shadow-atlas-ladder-active',
            isFilled &&
              'border border-atlas-dim/50 bg-atlas-proven-soft text-neutral-900 opacity-70',
            // -- Muted: still silver; dark text ~15% brighter than active ink
            isMuted &&
              'border border-atlas-dim/40 bg-atlas-proven text-[#3a3a3a] opacity-55'
          ),
        // -- Debunked black (−1 ETS): keep red glow when selected negative
        variant === 'debunked' &&
          cn(
            'bg-atlas-debunked text-atlas-muted',
            isActive &&
              (useRed
                ? 'z-[1] border-2 border-red-400 text-atlas-bright-text shadow-atlas-ladder-red'
                : 'z-[1] border-2 border-atlas-dim text-atlas-bright-text shadow-atlas-ladder-active'),
            isFilled &&
              'border border-red-900/40 text-atlas-muted opacity-38 shadow-none',
            isMuted && 'border-transparent opacity-[0.14] grayscale'
          ),
        isActive &&
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atlas-bloom focus-visible:ring-offset-2 focus-visible:ring-offset-atlas-void',
        className
      )}
      tabIndex={isActive && justification ? 0 : undefined}
      role={isActive ? 'group' : undefined}
      aria-label={
        isActive
          ? `${formatSigned(score)} ${label}${justification ? '. Justification available.' : ''}`
          : undefined
      }
    >
      <span
        className={cn(
          'shrink-0 font-display font-black leading-none',
          isActive ? 'text-xl sm:text-2xl md:text-3xl' : 'text-lg sm:text-xl md:text-2xl',
          variant === 'silver' && !isMuted && 'text-neutral-900',
          variant === 'silver' && isMuted && 'text-[#3a3a3a]',
          variant === 'debunked' && isActive && 'text-atlas-white',
          useRed && isActive && 'text-red-50',
          isZero && 'text-atlas-muted',
          isZero && isActive && 'text-atlas-bright-text'
        )}
      >
        {formatSigned(score)}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 text-left font-bold leading-snug',
          isActive ? 'text-xs sm:text-sm md:text-base' : 'text-[11px] sm:text-xs md:text-sm',
          // -- Silver: near-black on lit metal; slightly brighter dark grey when muted
          variant === 'silver' && !isMuted && 'text-neutral-900',
          variant === 'silver' && isMuted && 'text-[#3a3a3a]',
          variant !== 'silver' && isMuted && 'text-atlas-muted',
          variant !== 'silver' && isFilled && !isActive && 'text-atlas-muted',
          isZero && 'text-atlas-muted'
        )}
      >
        {label}
      </span>
    </div>
  );

  if (!isActive || !justification) {
    return box;
  }

  return (
    <Tooltip.Root delayDuration={200}>
      <Tooltip.Trigger asChild>{box}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          className="z-[100] max-w-sm rounded-atlas-panel border border-atlas-border bg-atlas-void p-3 text-xs leading-relaxed text-atlas-muted shadow-atlas-glow-md"
          sideOffset={8}
        >
          <p className="font-semibold text-atlas-bright-text">Justification</p>
          <p className="mt-1">{justification}</p>
          <Tooltip.Arrow className="fill-atlas-border" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
